import type { ReactNode } from "react";

export interface ProfileField {
  label: string;
  value: ReactNode;
}

interface ProfileSectionCardProps {
  title: string;
  testId: string;
  fields?: ProfileField[];
  /** Shown beside the title, e.g. the section's Edit button. */
  action?: ReactNode;
  /** While set, shown instead of the fields and children. */
  editForm?: ReactNode;
  children?: ReactNode;
}

/**
 * One profile section, showing its fields or, while editing, its form. `ph-no-capture` and `sentry-block` keep the
 * card's contents out of PostHog and Sentry session recordings: profiles hold
 * NI numbers and health data. Every section renders through this card, so a
 * new section is masked without anyone having to remember to.
 */
export function ProfileSectionCard({
  title,
  testId,
  fields,
  action,
  editForm,
  children,
}: ProfileSectionCardProps) {
  return (
    <section
      aria-label={title}
      data-testid={testId}
      className="ph-no-capture sentry-block @container flex w-full flex-col items-start gap-3 rounded-md border border-neutral-200 p-4"
    >
      <div className="flex w-full items-center justify-between gap-2">
        <h2 className="text-heading-3 font-heading-3 text-default-font">
          {title}
        </h2>
        {action}
      </div>
      {editForm || (
        <>
          {fields && (
            <dl className="grid w-full grid-cols-1 gap-x-8 gap-y-3 @sm:grid-cols-2">
              {fields.map((field) => (
                <div
                  key={field.label}
                  className="flex min-w-0 flex-col gap-0.5"
                >
                  <dt className="text-caption font-caption text-subtext-color">
                    {field.label}
                  </dt>
                  <dd className="text-body font-body text-default-font whitespace-pre-wrap break-words">
                    {field.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {children}
        </>
      )}
    </section>
  );
}
